import { Context, createContext, CSSProperties } from "react";
import { ColDef, CellClickedEvent, MenuItemDef, GridApi, RowNode, Column, ColumnApi } from "ag-grid-community";
import { WithOptional } from "../../laco-common/helpers/extendedTypes";

export type ICorreColDefParams = {
	suppressContextMenu?: boolean;
	getUrl?: (data: any) => string;
	isClickActionCol?: boolean;
	ignoreInDetailView?: boolean;
	ignoreInDetailViewFn?: (data) => boolean;
	hideInDetailView?: boolean;
	quickViewRenderContentOnly?: boolean;
	omitInGridView?: boolean;
	displayInListView?: boolean;
	displayHeaderInListView?: boolean;
	listViewRenderer?: (params: { value: any; data: any; colDef: CorreColDef }) => React.ReactNode | string;
	listViewCellClass?: string;
	listViewOmitCellClass?: boolean;
	isActiveSortCol?: boolean;
	isHiddenActiveSortCol?: boolean;
	sortDisplayRaw?: boolean;
	isInteger?: boolean;
	isPercentage?: boolean;
	useFormattedValueInEdit?: boolean;
	getCustomMenuOptions?: (api: GridApi) => MenuItemDef[];
};

export type CorreColDef = ColDef & {
	field: string;
	colId: string;
	type?: CorreColDataType;
} & ICorreColDefParams;

export type CorreColDataType = "numericColumn" | "dateColumn" | "booleanColumn";

export type CorreCellClicked = WithOptional<CellClickedEvent, "rowIndex" | "rowPinned" | "type">;

export type FieldViewData = {
	display: string;
	value: any;
	formattedValue: string | React.ReactNode;
	className?: string;
	style?: CSSProperties;
	headerClassName?: string;
	isEmpty?: boolean;
	onClick?;
};
export type FormattedViewData = { [field: string]: FieldViewData };

export interface IFormattedViewContext {
	viewData: FormattedViewData;
	loading?: boolean;
	hideLoadingText?: boolean;
	// dataLayout?: ILayoutFormat;
	omitLabels?: boolean;
}

export const FormattedViewContext: Context<IFormattedViewContext> = createContext<IFormattedViewContext>({
	viewData: null,
});

export type IParams = {
	value: any;
	getValue: (field: string) => any;
	colDef: ColDef;
	data: any;
	rowIndex?: number;
	rowData?: any[];
};

export type INumericStringParams = {
	minimumFractionDigits?: number;
	maximumFractionDigits?: number;
	useGrouping?: boolean;
	style?: "decimal" | "percent" | "currency" | "unit";
};

export type CorreNumberFormatter = (value: number, params?: INumericStringParams) => string;

export const QUICK_VIEW_COL_ID: string = "CMS-SYSTEM-QUICK-VIEW";
export const EDIT_COL_ID: string = "CMS-EDIT-DATA-COL";
export const INDEX_COL_ID: string = "CMS-INDEX-COL";
export const ACTIONS_COL_ID: string = "Actions";
export const CHECKBOX_COL_ID: string = "selection-checkbox";

export const isSystemCol = (colDef: ColDef): boolean =>
	colDef.colId === CHECKBOX_COL_ID ||
	colDef.colId === QUICK_VIEW_COL_ID ||
	colDef.colId === EDIT_COL_ID ||
	colDef.colId === INDEX_COL_ID;
export const isSystemColExtended = (colDef: CorreColDef, withActions?: boolean): boolean =>
	isSystemCol(colDef) ||
	(!withActions && colDef.colId === ACTIONS_COL_ID) ||
	colDef.colId === "created_by" ||
	colDef.colId === "updated_by";

export function getColDefById(colId: string, colDefs: CorreColDef[]): CorreColDef {
	return colDefs.find((colDef: CorreColDef) => colDef.colId === colId);
}

export function getColDefByField(field: string, colDefs: CorreColDef[]): CorreColDef {
	return colDefs.find((colDef: CorreColDef) => colDef.field === field);
}

export const getMergedClassProps = (colDef: CorreColDef, className: string) => {
	let classParams = {};
	if (colDef.editable === true) {
		classParams = {
			cellClass: `corre-editable ${className} ` + (colDef.cellClass || ""),
		};
	} else {
		// editable is conditional, it's a callback
		classParams = {
			cellClassRules: {
				...(colDef.cellClassRules || {}),
				"corre-editable": colDef.editable,
				className: true,
			},
		};
	}
	return classParams;
};

// TODO: get from ag-grid when exported correctly
export interface BaseColDefParams {
	node: RowNode;
	data: any;
	colDef: ColDef;
	column: Column;
	api: GridApi | null | undefined;
	columnApi: ColumnApi | null | undefined;
	context: any;
}
